// Internal routing helpers for the wiki section of the quiz app.
// Replaces the old external URL builder (wikiUrl.ts) that pointed at
// wiki.actuarialnotes.com; everything now resolves to /wiki/... routes.

import { SHARED_RESOURCE_FOLDERS, resourceRepoPath, splitResourcePath } from './resourceFolders'

export type WikiEntryKind = 'concept' | 'resource' | 'exam' | 'event' | 'regulation' | 'guide'

export interface WikiEntryRef {
  kind: WikiEntryKind
  name: string
  // Explicit repo path, used when the file can't be derived from kind+name
  // (e.g. a `type: event` file that lives in Resources/Regulation/). When set,
  // entryRefToRepoPath() returns it verbatim.
  path?: string
}

// "Expected Value" → "Expected+Value" (matches Obsidian Publish slugs, keeps
// existing `wiki_link` values in question frontmatter working).
function toSlug(name: string): string {
  // encodeURIComponent first (encodes special chars), then swap %20 → +.
  // Doing the space→+ replacement before encodeURIComponent would cause +
  // to be double-encoded as %2B, breaking fromSlug round-trips.
  return encodeURIComponent(name.trim()).replace(/%20/g, '+')
}

export function fromSlug(slug: string): string {
  return decodeURIComponent(slug.replace(/\+/g, ' '))
}

export function wikiRoute(ref: WikiEntryRef): string {
  return `/wiki/${ref.kind}/${toSlug(ref.name)}`
}

/** The Study Guides tab's shelf of syllabus readings (`pages/wiki/WikiResources.tsx`). */
export const RESOURCES_ROUTE = '/wiki/resources'

/**
 * The Study Guides tab's two pages, which the sidebar lists under it the way
 * it lists an open project's views under Projects: **Exams** — the exam ladder
 * at `/wiki`, with the exam and concept pages read from it — and **Resources**,
 * the shelf, with the resource pages opened from it.
 */
export type StudyGuidesSection = 'exams' | 'resources'

/** Which of the Study Guides pages `pathname` sits under, or null outside the tab. */
export function studyGuidesSection(pathname: string): StudyGuidesSection | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
  if (path === RESOURCES_ROUTE || path.startsWith('/wiki/resource/')) return 'resources'
  if (path === '/wiki' || path.startsWith('/wiki/')) return 'exams'
  return null
}

// Convert a repo-relative file path ("Concepts/Expected Value.md",
// "Resources/Books/Probability for Risk Management.md",
// "Exam P-1 (SOA).md") to its wiki route and vice versa.
export function pathToEntryRef(path: string): WikiEntryRef | null {
  const p = path.replace(/^\/+/, '').replace(/\.md$/i, '')
  if (p.toLowerCase().startsWith('concepts/')) {
    return { kind: 'concept', name: p.slice('concepts/'.length) }
  }
  if (p.toLowerCase().startsWith('resources/books/')) {
    return { kind: 'resource', name: p.slice('resources/books/'.length) }
  }
  // A resource page in one of the type folders (Standards/, Reports/, …) — or a
  // shared one, once the bundle has registered it as a resource page there.
  const split = splitResourcePath(`${p}.md`)
  if (split && (!SHARED_RESOURCE_FOLDERS.includes(split.folder) || resourceRepoPath(split.name) === `${p}.md`)) {
    return { kind: 'resource', name: split.name, path: `${p}.md` }
  }
  if (p.toLowerCase().startsWith('resources/events/')) {
    return { kind: 'event', name: p.slice('resources/events/'.length), path: `${p}.md` }
  }
  if (p.toLowerCase().startsWith('resources/regulation/')) {
    return { kind: 'regulation', name: p.slice('resources/regulation/'.length), path: `${p}.md` }
  }
  if (p.toLowerCase().startsWith('resources/benchmarks/')) {
    return { kind: 'resource', name: p.slice('resources/benchmarks/'.length), path: `${p}.md` }
  }
  // An exam-page orientation tip: Guides/<exam page>/<tip>.md. The name is the
  // tip alone — the folder is what says which exam it belongs to — so the path
  // has to travel with the ref ("Scoring" is a page under every exam).
  if (p.toLowerCase().startsWith('guides/')) {
    const title = p.split('/').pop() ?? p
    return { kind: 'guide', name: title, path: `${p}.md` }
  }
  if (/^Exam[ -]/i.test(p)) {
    return { kind: 'exam', name: p }
  }
  return null
}

export function entryRefToRepoPath(ref: WikiEntryRef): string {
  if (ref.path) return ref.path
  switch (ref.kind) {
    case 'concept':
      return `Concepts/${ref.name}.md`
    case 'resource':
      return resourceRepoPath(ref.name)
    case 'exam':
      return `${ref.name}.md`
    case 'event':
      return `Resources/Events/${ref.name}.md`
    case 'regulation':
      return `Resources/Regulation/${ref.name}.md`
    // Only reachable for a guide ref built without its path; a real one always
    // carries the exam folder it lives in and returns above.
    case 'guide':
      return `Guides/${ref.name}.md`
  }
}

// Normalise the many forms a wiki reference can take — Obsidian wikilinks,
// plus/encoded slugs, absolute or relative URLs — into a WikiEntryRef.
// Returns null when the href doesn't look like an internal wiki target.
export function hrefToEntryRef(href: string): WikiEntryRef | null {
  if (!href) return null

  // Strip protocol/host for known wiki domains.
  let clean = href
  try {
    const u = new URL(href, 'https://placeholder.local')
    if (
      u.host === 'wiki.actuarialnotes.com' ||
      u.host === 'placeholder.local' // relative
    ) {
      clean = u.pathname + u.search + u.hash
    } else {
      return null
    }
  } catch {
    /* non-URL string, treat as path */
  }

  // Drop fragment / query, trim leading slashes, turn + back into spaces.
  clean = clean.split('#')[0]!.split('?')[0]!.replace(/^\/+/, '').replace(/\+/g, ' ')
  if (!clean) return null

  // Already slugged as concept/resource/exam internal route?
  const internal = clean.match(/^wiki\/(concept|resource|exam|event|regulation|guide)\/(.+)$/i)
  if (internal) {
    return { kind: internal[1].toLowerCase() as WikiEntryKind, name: fromSlug(internal[2]) }
  }

  const decoded = decodeURIComponent(clean)
  const asPath = pathToEntryRef(decoded)
  if (asPath) return asPath

  // A resource page in a type folder the bundle has registered
  // (`[[IFRS 17]]` → Resources/Standards/IFRS 17.md).
  const resourcePath = resourceRepoPath(decoded)
  if (!resourcePath.startsWith('Resources/Books/')) return { kind: 'resource', name: decoded, path: resourcePath }

  // Otherwise assume it's a bare concept name (the common [[Expected Value]] case).
  return { kind: 'concept', name: decoded }
}

// Canonical short exam id used as a localStorage key (`actuarial-notes-learned`)
// and as the `?from=` query param on concept routes. Accepts any of:
//   "Exam P-1 (SOA)"   (filename)          → "p-1"
//   "Exam P-1 (SOA).md"                    → "p-1"
//   "Exam P"           (syllabus label)    → "p-1"   (assumes -1 suffix)
//   "P-1"              (raw id)            → "p-1"
// Fills in the -N suffix when missing because publish.js keys always carry it.
export function examIdFromFile(name: string): string {
  const cleaned = name
    .replace(/\.md$/i, '')
    .replace(/^Exam\s+/i, '')
    .replace(/\s*\([^)]*\)\s*$/, '')
    .trim()
  if (!cleaned) return name.toLowerCase()
  const withDash = cleaned.includes('-') ? cleaned : `${cleaned}-1`
  return withDash.toLowerCase()
}

// What a body requires but doesn't call an exam — the VEE credits, the DISC
// courses, the SOA's e-learning modules and ATPA assessment, the professionalism
// courses — is filed as `Exam … (Body).md` like every syllabus page (the
// collectors read `Exam*.md`), and keeps that label wherever it is a quiz bank's
// name (`Exam DISC-DA`). Only a list of requirements titles it without "Exam".
const NON_EXAM_REQUIREMENT_RE = /^(?:VEE-[A-Z]+|DISC-[A-Z]+|PAF|ASF|ATPA|FAP|APC|DMAC|FAC|COP|[A-Z]+-M\d)$/

// A CIA module's id (`PC-M1`, `ACIA-M2`) and the name the CIA gives it.
const CIA_MODULE_RE = /^([A-Z]+)-M(\d)$/

/** True for a page's exam id (`VEE-MS`, `DISC-DA`, `COP`) that names no exam. */
export function isNonExamRequirement(examId: string): boolean {
  return NON_EXAM_REQUIREMENT_RE.test(examId)
}

// The title a requirement's row carries on the Study Guides page: the exam's
// display name, or for a non-exam requirement the requirement on its own.
//   "Exam MAS-I (CAS).md" → "Exam MAS-I"
//   "Exam VEE-MS (SOA).md" → "VEE-MS"
//   "Exam PC-M1 (CIA).md"  → "PC Module 1"
//   "Exam ACIA-CAPSTONE (CIA).md" → "ACIA Capstone Exam"
export function requirementDisplayName(name: string): string {
  const label = examDisplayName(name)
  const id = label.replace(/^Exam\s+/i, '')
  // The CIA's Capstone has a name of its own, which its id only abbreviates.
  if (id === 'ACIA-CAPSTONE') return 'ACIA Capstone Exam'
  if (!isNonExamRequirement(id)) return label
  const module = id.match(CIA_MODULE_RE)
  return module ? `${module[1]} Module ${module[2]}` : id
}

// Display name for an exam page: the file name without its `.md` extension and
// without the examining-body suffix the vault files carry.
//   "Exam P-1 (SOA).md"  → "Exam P-1"
//   "Exam MAS-I (CAS)"   → "Exam MAS-I"
// The quiz builder already labels exams this way (`Exam P-1`, `Exam 5`), so the
// study-guide cards use this to match.
export function examDisplayName(name: string): string {
  const cleaned = name
    .replace(/\.md$/i, '')
    .replace(/\s*\((?:SOA|CAS|CIA)\)\s*$/i, '')
    .trim()
  return cleaned || name
}
